/** Shared nominee identification type options used by ProfileForm & ProfileJoint */
export const NOMINEE_IDENTIFICATION_LIST = [
  {
    display: "PAN Card no",
    value: 5,
  },
  {
    display: "Aadhar Card no",
    value: 2,
  },
  {
    display: "Saving Bank account no.",
    value: 3,
  },
  {
    display: "Demat Account ID",
    value: 4,
  },
  {
    display: "Photo and Signature of the Nominee/guardian",
    value: 1,
  },
];

const GUARDIAN_ERROR_KEYS = [
  "guardTitle",
  "guardFullName",
  "guardRelation",
  "guardOtherRelation",
  "guardEduQualification",
  "guardOccupation",
  "guardAnnualIncome",
  "guardPlaceOfBirth",
  "guardNetWorth",
  "guardidentificationDetail",
  "guardIdentificationDetailValue",
] as const;

/** Title stays optional; other guardian field errors still block submit */
const OPTIONAL_GUARDIAN_ERROR_KEYS = new Set([""]);

function isFilled(value: unknown): boolean {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function hasGuardianAddressData(address: Record<string, unknown> | undefined): boolean {
  if (!address || typeof address !== "object") return false;
  return Object.entries(address).some(
    ([key, value]) => key !== "Area" && isFilled(value)
  );
}

function isGuardianAddressIncomplete(
  address: Record<string, unknown> | undefined
): boolean {
  if (!address || typeof address !== "object") return true;
  return Object.entries(address)
    .filter(([key]) => key !== "Area")
    .some(([, value]) => !isFilled(value));
}

/** True when identification type is selected and its value is required but missing */
function isIdentificationValueMissing(identificationDetail: any): boolean {
  const idName = identificationDetail?.name;
  const idValue = identificationDetail?.value;
  return (
    isFilled(idName) &&
    idName !== "Photo and Signature of the Nominee/guardian" &&
    !isFilled(idValue)
  );
}

/** True when the user has entered any guardian detail (section started). */
export function hasAnyGuardianDetails(guardianDetails: any): boolean {
  if (!guardianDetails || typeof guardianDetails !== "object") return false;

  const textKeys = [
    "guardTitle",
    "guardFullName",
    "guardRelation",
    "guardRelationOther",
    "guardEduQualification",
    "guardOccupation",
    "guardAnnualIncome",
    "guardPlaceOfBirth",
    "guardNetWorth",
    "dob",
  ] as const;

  if (textKeys.some((key) => isFilled(guardianDetails[key]))) {
    return true;
  }

  if (hasGuardianAddressData(guardianDetails.guardianAddress)) {
    return true;
  }

  const idName = guardianDetails?.identificationDetail?.name;
  const idValue = guardianDetails?.identificationDetail?.value;
  if (isFilled(idName) || isFilled(idValue)) {
    return true;
  }

  return false;
}

/**
 * When nominee is minor, guardian details are mandatory except title.
 * Additional identification type is optional; if selected, its value is required.
 */
export function isGuardianDetailsInvalid(element: any): boolean {
  if (!element?.isNomineeMinor) return false;

  const guardian = element?.guardianDetails || {};

  // Mandatory fields (title excluded)
  if (!isFilled(guardian.guardFullName)) return true;
  if (!isFilled(guardian.guardRelation)) return true;
  if (
    guardian.guardRelation === "13" &&
    !isFilled(guardian.guardRelationOther)
  ) {
    return true;
  }
  if (!isFilled(guardian.guardEduQualification)) return true;
  if (!isFilled(guardian.guardOccupation)) return true;
  if (!isFilled(guardian.guardAnnualIncome)) return true;
  if (!isFilled(guardian.guardPlaceOfBirth)) return true;
  if (
    !isFilled(guardian.guardNetWorth) ||
    parseInt(String(guardian.guardNetWorth), 10) < 1000
  ) {
    return true;
  }
  if (!isFilled(guardian.dob)) return true;
  if (isGuardianAddressIncomplete(guardian.guardianAddress)) return true;

  const errors = element?.error || {};
  if (
    GUARDIAN_ERROR_KEYS.some(
      (key) => !OPTIONAL_GUARDIAN_ERROR_KEYS.has(key) && !!errors[key]
    )
  ) {
    return true;
  }
  if (errors?.guardianDetails?.dob) {
    return true;
  }

  // Identification type optional; value mandatory only when type is selected
  if (isIdentificationValueMissing(guardian.identificationDetail)) {
    return true;
  }

  return false;
}

/** Core nominee required-field check (email and mobile are optional). */
export function isNomineeCoreInvalid(element: any): boolean {
  if (!element || !Object.keys(element).length) return true;

  const guardianErrorKeySet = new Set<string>(
    GUARDIAN_ERROR_KEYS as unknown as string[]
  );

  // Format errors on optional fields (email/mobile/%) still block submit.
  // identificationDetail type error should not block when type is optional.
  const hasBlockingError =
    element?.error &&
    Object.entries(element.error).some(([key, error]) => {
      if (guardianErrorKeySet.has(key) || key === "guardianDetails") return false;
      if (key === "identificationDetail") return false;
      return !!error;
    });

  // Identification type optional; value mandatory only when type is selected
  if (isIdentificationValueMissing(element?.identificationDetail)) {
    return true;
  }

  return (
    !element?.nomTitle ||
    !element?.nomFullName ||
    element?.nomFullName === "" ||
    !element?.nomRelation ||
    element?.nomRelation === "" ||
    (element?.nomRelation === "13" &&
      element?.nomRelationOther !== undefined &&
      element?.nomRelationOther === "") ||
    !element?.nomDob ||
    !isFilled(element?.nomPerSharing) ||
    hasBlockingError 
    // Object.values(element?.nomineeAddress || {}).some((value) => !value)
  );
}

/** Maps IAddressFormat keys to user-readable labels. */
export const ADDRESS_FIELD_LABELS: Record<string, string> = {
  addressLine1: "Address Line 1",
  City: "City",
  Country: "Country",
  District: "District",
  PinCode: "PIN Code",
  State: "State",
};

/**
 * Returns display names of missing/invalid nominee fields.
 * Uses the same field checks as isNomineeCoreInvalid.
 */
export function getMissingNomineeFields(element: any): string[] {
  if (!element || !Object.keys(element).length) return ["All nominee fields"];
  const missing: string[] = [];
  if (!element?.nomTitle) missing.push("Title");
  if (!element?.nomFullName || element?.nomFullName === "") missing.push("Name");
  if (!element?.nomRelation || element?.nomRelation === "") {
    missing.push("Relationship");
  } else if (
    element?.nomRelation === "13" &&
    element?.nomRelationOther !== undefined &&
    element?.nomRelationOther === ""
  ) {
    missing.push("Other Relationship");
  }
  if (!element?.nomDob) missing.push("Date of Birth");
  if (!isFilled(element?.nomPerSharing)) missing.push("Share %");
  if (isIdentificationValueMissing(element?.identificationDetail)) missing.push("Identification Value");
  return missing;
}

/**
 * Returns display names of missing/invalid guardian fields for a minor nominee.
 * Uses the same field checks as isGuardianDetailsInvalid.
 */
export function getMissingGuardianFields(element: any): string[] {
  if (!element?.isNomineeMinor) return [];
  const guardian = element?.guardianDetails || {};
  const missing: string[] = [];
  if (!isFilled(guardian.guardFullName)) missing.push("Name");
  if (!isFilled(guardian.guardRelation)) {
    missing.push("Relationship");
  } else if (guardian.guardRelation === "13" && !isFilled(guardian.guardRelationOther)) {
    missing.push("Other Relationship");
  }
  if (!isFilled(guardian.guardEduQualification)) missing.push("Educational Qualification");
  if (!isFilled(guardian.guardOccupation)) missing.push("Occupation");
  if (!isFilled(guardian.guardAnnualIncome)) missing.push("Annual Income");
  if (!isFilled(guardian.guardPlaceOfBirth)) missing.push("Place of Birth");
  if (
    !isFilled(guardian.guardNetWorth) ||
    parseInt(String(guardian.guardNetWorth), 10) < 1000
  ) {
    missing.push("Net Worth (min. ₹1,000)");
  }
  if (!isFilled(guardian.dob)) missing.push("Date of Birth");
  if (isGuardianAddressIncomplete(guardian.guardianAddress)) missing.push("Address");
  if (isIdentificationValueMissing(guardian.identificationDetail)) missing.push("Identification Value");
  return missing;
}

/**
 * Returns true when nominee validation fails (invalid / incomplete).
 * Matches historical validateNominees() return semantics.
 */
export function validateNomineesInvalid(
  showNominee: boolean,
  numOfNominee: number,
  nomiDetails: any[]
): boolean {
  try {
    console.log("show nomiee", showNominee, nomiDetails);
    
    if (showNominee && numOfNominee === 0) {
      return true;
    }
    if (showNominee && nomiDetails && nomiDetails.length > 0) {
      for (let i = 0; i < nomiDetails.length; i++) {
        const element = nomiDetails[i];
        console.log("element", element);
        
        if (isNomineeCoreInvalid(element)) {
          return true;
        }
        if (isGuardianDetailsInvalid(element)) {
          return true;
        }
      }
      return false;
    }
    return false;
  } catch {
    return false;
  }
}

/** Whether any nominee share % has been entered (non-empty). */
export function hasAnyNomineeSharePercentage(nomiDetails: any[]): boolean {
  return (nomiDetails || []).some((n) => isFilled(n?.nomPerSharing));
}

/** Build identification detail object; clears prior value when type changes. */
export function buildIdentificationDetail(
  option: { display?: string; value?: number } | undefined,
  partyLabel?: string
): { name?: string; value?: string } {
  if (!option?.display) return {};
  if (option.display === "Photo and Signature of the Nominee/guardian") {
    return {
      name: option.display,
      value: partyLabel || "",
    };
  }
  return { name: option.display, value: "" };
}
